/**
 * Who may see a private ticket — the client's reading of `item_visible()`.
 *
 * The database is the authority: `read_items` and `update_items` both call
 * `item_visible()`, so nothing here grants or denies anything. What it decides
 * is whether the lock button is offered, because `update_items`' WITH CHECK
 * refuses an update that would leave a ticket invisible to the person making
 * it — you cannot mark a ticket private unless you would still see it
 * afterwards. Without this the refusal arrives as a bare RLS error on save,
 * which is the surprise the rule exists to prevent.
 *
 * Kept in one module, and tested against the same cases as the policy, because
 * a second copy of a visibility rule is the thing that drifts. If you change
 * an arm here, change it in 0049's `item_visible()` too — or, better, look at
 * whether the question can be asked of the server instead.
 */

export interface TicketVisibilityFacts {
  /** `backlog_items.added_by_id` — the filer. */
  addedById: string | null;
  /** `backlog_items.assigned_to_id`, normalised to an array (see pgArray). */
  assignedToIds: string[] | null;
  /** `backlog_items.assigned_team_id` — the owning queue, not the team tags. */
  assignedTeamId: string | null;
}

export interface TicketViewer {
  userId: string | null;
  /** admin OR superadmin, matching `is_admin()`. */
  isAdmin: boolean;
  /** Team ids from the `my_teams()` RPC — the same source the policy reads. */
  myTeamIds: string[];
}

/**
 * Whether this viewer would still see the ticket once it is private, which is
 * exactly the condition for being allowed to make it private.
 */
export const canSeeWhenPrivate = (ticket: TicketVisibilityFacts, viewer: TicketViewer): boolean => {
  // Order mirrors item_visible(): the cheapest and broadest arm first.
  if (viewer.isAdmin) return true;
  if (!viewer.userId) return false;
  if (ticket.addedById === viewer.userId) return true;
  if (ticket.assignedToIds?.includes(viewer.userId)) return true;
  if (ticket.assignedTeamId !== null && viewer.myTeamIds.includes(ticket.assignedTeamId)) return true;
  return false;
};

/**
 * Hover text for the lock when this viewer may not use it. Names who can,
 * rather than saying "not allowed" — the reader's next question is who to ask.
 */
export const LOCK_DENIED_TIP =
  "Only the person who filed it, its assignees, its owning team, or an admin can make this ticket private — it would leave your own list the moment it locked.";

/**
 * Hover text for the read-only lock in the list, where there is nothing to
 * click — same facts as the toggle's tip, without the instruction.
 */
export const PRIVATE_BADGE_TIP = "Only the filer, its assignees, its owning team, and admins can see this ticket.";

/** Hover text for the lock in each state it can actually be clicked in. */
export const LOCK_TIP_PUBLIC = "Anyone in the workspace can see this ticket. Click to make it private.";
export const LOCK_TIP_PRIVATE =
  "Private: only the filer, its assignees, its owning team, and admins can see it. Click to unlock.";
